import { PlusCircleOutlined } from "@ant-design/icons"
import { ExpenseKindEnum, ExpenseRequestTypeEnum, RightEnum, SystemRightEnum } from "@finway-group/shared/lib/models"
import { Button, Dropdown } from "antd"
import { MenuDividerType } from "antd/lib/menu/hooks/useItems"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { DiamondBoxedBlueIcon } from "Components/icons/DiamondIcon"
import { NewCostCenter1Icon } from "Components/icons/NewCostCenter1Icon"
import { NewEmployeeIcon } from "Components/icons/NewEmployeeIcon"
import { NewInvoiceIcon } from "Components/icons/NewInvoiceIcon"
import { NewPurchaseIcon } from "Components/icons/NewPurchaseIcon"
import { NewReimbursementIcon } from "Components/icons/NewReimbursementIcon"
import { NewSubscriptionIcon } from "Components/icons/NewSubscriptionIcon"
import { NewTripFolderIcon } from "Components/icons/NewTripFolderIcon"
import { NewVendorIcon } from "Components/icons/NewVendorIcon"
import { ExpenseCreateFormModal } from "Components/modals/expenseCreateForm.modal"
import { UserModal } from "Components/modals/user.modal"
import { VendorFormModal } from "Components/modals/vendor.modal"
import { CostCenterFormModal } from "Features/pages/costCenters/costCenterForm/CostCenterFormModal"
import { PurchaseOrderDrawer } from "Features/pages/purchaseOrders/drawer/PurchaseOrderDrawer"
import { useDrawer } from "Features/pages/purchaseOrders/hooks/useDrawer.hook"
import { useCompany } from "Shared/hooks/company.hooks"
import { useRequestActionDisabled } from "Shared/hooks/expense.hooks"
import { useIsPurchaseOrderEnabled, useIsTravelEnabled } from "Shared/hooks/featureFlags.hooks"
import { AuthzService, VendorService } from "Shared/services"
import { insertIf } from "Shared/utils/helper.utils"
import { getPopupAnchor } from "Shared/utils/popup.utils"

import { DeliveryNoteDrawer } from "Features/pages/deliveryNotes/components/DelieryNoteDrawer"
import { NewDeliveryNoteIcon } from "Features/pages/deliveryNotes/icons/NewDeliveryNoteIcon"
import { DeliveryNoteFormValues } from "Features/pages/deliveryNotes/types/deliveryNote.interfaces"
import { PurchaseOrderFormValues } from "Features/pages/purchaseOrders/types/PurchaseOrder.interfaces"
import { MenuItemInterface, NavbarMenuItemEnum } from "./types"

export const ActionMenuSidebar = () => {
    const { t } = useTranslation()

    const [isExpenseModalShowing, setIsExpenseModalShowing] = useState(false)
    const [isVendorModalShowing, setIsVendorModalShowing] = useState<boolean>(false)
    const [isCostCenterModalShowing, setIsCostCenterModalShowing] = useState<boolean>(false)
    const [isUserModalShowing, setIsUserModalShowing] = useState<boolean>(false)

    const [requestType, setRequestType] = useState<ExpenseRequestTypeEnum>(ExpenseRequestTypeEnum.PURCHASE)
    const [isReimbursement, setIsReimbursement] = useState<boolean>(false)

    const { isVendorCreationRestricted } = useCompany()

    const requestButtonsDisabled = useRequestActionDisabled()

    const isTravelEnabled = useIsTravelEnabled()
    const isPurchaseOrderEnabled = useIsPurchaseOrderEnabled()
    const isAllowedToCreateExpenses = AuthzService.isLoggedInUserAllowedToCreateExpenses()

    const { isDrawerOpen: isPurchaseOrderDrawerOpen, closeDrawer: closePurchaseOrderDrawer, openDrawer: openPurchaseOrderDrawer } = useDrawer<PurchaseOrderFormValues>()
    const { isDrawerOpen: isDeliveryNoteDrawerOpen, closeDrawer: closeDeliveryNoteDrawer, openDrawer: openDeliveryNoteDrawer } = useDrawer<DeliveryNoteFormValues>()

    const expenseMenuItems: Array<MenuItemInterface> = requestButtonsDisabled
        ? []
        : [
              {
                  link: NavbarMenuItemEnum.PURCHASE_ORDER,
                  label: t("label:purchase_order"),
                  canView: isAllowedToCreateExpenses,
                  icon: <NewPurchaseIcon />,
                  ...(!isPurchaseOrderEnabled && { afterLabel: <DiamondBoxedBlueIcon /> }),
              },
              {
                  link: NavbarMenuItemEnum.DELIVERY_NOTE,
                  label: t("label:delivery_note"),
                  canView: isAllowedToCreateExpenses,
                  icon: <NewDeliveryNoteIcon />,
                  ...(!isPurchaseOrderEnabled && { afterLabel: <DiamondBoxedBlueIcon /> }),
              },
              {
                  link: NavbarMenuItemEnum.INVOICE_REQUEST,
                  label: t("label:invoice"),
                  canView: isAllowedToCreateExpenses,
                  icon: <NewInvoiceIcon />,
              },
              {
                  link: NavbarMenuItemEnum.SUBSCRIPTION,
                  label: t("label:subscription"),
                  canView: isAllowedToCreateExpenses,
                  icon: <NewSubscriptionIcon />,
              },
              {
                  link: NavbarMenuItemEnum.PURCHASED_REQUEST,
                  label: t("label:purchase_request"),
                  canView: isAllowedToCreateExpenses,
                  icon: <NewPurchaseIcon />,
              },
              {
                  link: NavbarMenuItemEnum.REIMBURSEMENT_REQUEST,
                  label: t("label:reimbursement"),
                  canView: isAllowedToCreateExpenses,
                  icon: <NewReimbursementIcon />,
              },
              {
                  link: NavbarMenuItemEnum.TRIP_FOLDER_REQUEST,
                  label: t("label:trip_folder_request"),
                  canView: isAllowedToCreateExpenses && isTravelEnabled,
                  icon: <NewTripFolderIcon />,
              },
          ]

    const resourcesMenuItems: Array<MenuItemInterface> = [
        {
            link: NavbarMenuItemEnum.EMPLOYEE,
            label: t("label:employee"),
            canView: AuthzService.isSystemRightGrantedForLoggedInUser(SystemRightEnum.SYS__USER__ALL__CREATE),
            icon: <NewEmployeeIcon />,
        },
        {
            link: NavbarMenuItemEnum.COST_CENTER,
            label: t("label:cost_center"),
            canView: AuthzService.isRightGrantedForLoggedInUser(RightEnum.CC1__ALL__MANAGE),
            icon: <NewCostCenter1Icon />,
        },
        {
            link: NavbarMenuItemEnum.VENDOR,
            label: t("label:vendor"),
            canView: VendorService.shouldShowVendorButton(isVendorCreationRestricted),
            icon: <NewVendorIcon />,
        },
    ]

    const filteredExpenseMenuItems = expenseMenuItems.filter((menuItem) => menuItem.canView)
    const filteredResourcesMenuItems = resourcesMenuItems.filter((menuItem) => menuItem.canView)
    const isDividerNeeded = filteredExpenseMenuItems.length > 0 && filteredResourcesMenuItems.length > 0
    const menuItemCount = filteredExpenseMenuItems.length + filteredResourcesMenuItems.length
    if (menuItemCount === 0) {
        return null
    }

    const handleCreateMenuCommand = (command: NavbarMenuItemEnum): void => {
        switch (command) {
            case NavbarMenuItemEnum.PURCHASED_REQUEST:
                setRequestType(ExpenseRequestTypeEnum.PURCHASE)
                setIsExpenseModalShowing(true)
                break
            case NavbarMenuItemEnum.INVOICE_REQUEST:
                setRequestType(ExpenseRequestTypeEnum.INVOICE)
                setIsExpenseModalShowing(true)
                break
            case NavbarMenuItemEnum.SUBSCRIPTION:
                setRequestType(ExpenseRequestTypeEnum.SUBSCRIPTION)
                setIsExpenseModalShowing(true)
                break
            case NavbarMenuItemEnum.REIMBURSEMENT_REQUEST:
                setRequestType(ExpenseRequestTypeEnum.REIMBURSEMENT)
                setIsExpenseModalShowing(true)
                setIsReimbursement(true)
                break
            case NavbarMenuItemEnum.TRIP_FOLDER_REQUEST:
                setRequestType(ExpenseRequestTypeEnum.TRIP_FOLDER)
                setIsExpenseModalShowing(true)
                break
            case NavbarMenuItemEnum.EMPLOYEE:
                setIsUserModalShowing(true)
                break
            case NavbarMenuItemEnum.COST_CENTER:
                setIsCostCenterModalShowing(true)
                break
            case NavbarMenuItemEnum.VENDOR:
                setIsVendorModalShowing(true)
                break
            case NavbarMenuItemEnum.PURCHASE_ORDER:
                openPurchaseOrderDrawer()
                break
            case NavbarMenuItemEnum.DELIVERY_NOTE:
                openDeliveryNoteDrawer()
                break
            default:
                break
        }
    }

    const renderMenuItems = (menuItems: Array<MenuItemInterface>) =>
        menuItems.map(({ link, label, afterLabel, icon }) => ({
            key: link,
            label: (
                <>
                    {label} {afterLabel}
                </>
            ),
            icon,
            onClick: () => handleCreateMenuCommand(link),
            className: "flex items-center font-medium text-base",
        }))

    const initialKind = () => {
        switch (requestType) {
            case ExpenseRequestTypeEnum.TRIP_FOLDER:
                return ExpenseKindEnum.TRIP_FOLDER
            case ExpenseRequestTypeEnum.SUBSCRIPTION:
                return ExpenseKindEnum.SUBSCRIPTION
            default:
                return ExpenseKindEnum.ONE_TIME_EXPENSE
        }
    }

    return (
        <>
            <ExpenseCreateFormModal
                kind={initialKind()}
                isShowing={isExpenseModalShowing}
                isNew={true}
                type={requestType}
                onCancel={() => setIsExpenseModalShowing(false)}
                isReimbursement={isReimbursement}
            />
            <CostCenterFormModal isNew={true} isShowing={isCostCenterModalShowing} onHide={() => setIsCostCenterModalShowing(false)} />
            {isVendorModalShowing && <VendorFormModal isNew={true} isShowing={isVendorModalShowing} onCancel={setIsVendorModalShowing} />}

            <UserModal
                isNew={true}
                isShowing={isUserModalShowing}
                onCancel={() => {
                    setIsUserModalShowing(false)
                }}
            />

            <Dropdown
                placement="bottomLeft"
                className="flex items-center"
                trigger={["click"]}
                align={{ offset: [180, -36] }}
                overlayClassName="finway-action-menu-sidebar-dropdown z-20 w-220"
                menu={{
                    items: [
                        ...renderMenuItems(filteredExpenseMenuItems),
                        ...insertIf(isDividerNeeded, {
                            type: "divider",
                            className: "m-10",
                        } as MenuDividerType),
                        ...renderMenuItems(filteredResourcesMenuItems),
                    ],
                }}
                getPopupContainer={getPopupAnchor()}
            >
                <Button data-testid="sideBarNewButton" className="my-8 h-36 w-full bg-finway-green text-white">
                    <PlusCircleOutlined />
                    {t("action:create")}
                </Button>
            </Dropdown>

            <PurchaseOrderDrawer // keep line breaks
                open={isPurchaseOrderDrawerOpen}
                onClose={closePurchaseOrderDrawer}
                editMode={false}
            />
            <DeliveryNoteDrawer // keep line breaks
                open={isDeliveryNoteDrawerOpen}
                onClose={closeDeliveryNoteDrawer}
                editMode={false}
            />
        </>
    )
}
